import useDebounceValue from '@exodus/react-hooks/common/useDebounceValue';
import { useCallback, useState } from 'react';

import Subpage from '@/ui/components/subpage';
import { ALL_OPTION } from '@/ui/constants';
import exodus from '@/ui/exodus';
import useBaseAssets from '@/ui/hooks/use-base-assets';
import useChains from '@/ui/hooks/use-chains';
import useExpandedEffect from '@/ui/hooks/use-expanded-effect';
import { isAllOption } from '@/ui/utils/content';
import ConfigurableTokens from './components/configurable-tokens.js';
import Filters from './components/filters.js';
import SearchedTokens from './components/searched-tokens.js';
import useTokenSearch from './hooks/use-token-search.js';
import type { ChainNameSelection } from './types.js';

const CHAINS_FILTER = { withFeatures: ['customTokens'] } as const;

function ManageTokensPage() {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounceValue(query, 300);
  const [selectedChains, setSelectedChains] =
    useState<ChainNameSelection>(ALL_OPTION);

  const customTokenChains = useChains(CHAINS_FILTER);
  const allChains = useBaseAssets().map((asset) => asset.name);

  const { search, value: tokens, loading } = useTokenSearch();

  const handleSelectedChainsChange = useCallback(
    (value: ChainNameSelection) => {
      search(query, isAllOption(value) ? allChains : value);
      setSelectedChains(value);
    },
    [search, query, allChains],
  );

  const handleQueryChange = useCallback(
    (value: string) => {
      search(value, isAllOption(selectedChains) ? allChains : selectedChains);
      setQuery(value);
    },
    [allChains, search, selectedChains],
  );

  useExpandedEffect(() => {
    exodus.analytics.track({
      event: 'WalletAssetManagerView',
    });
  }, []);

  return (
    <Subpage
      title="Manage tokens"
      className="flex h-full flex-col items-center overflow-hidden"
    >
      <Filters
        chains={customTokenChains}
        selectedChains={selectedChains}
        onSelectedChainsChange={handleSelectedChainsChange}
        query={query}
        onQueryChange={handleQueryChange}
      />

      {debouncedQuery.length < 3 ? (
        <ConfigurableTokens
          chains={allChains}
          selectedChains={selectedChains}
        />
      ) : (
        <SearchedTokens tokens={tokens} loading={loading} />
      )}
    </Subpage>
  );
}

export default ManageTokensPage;
